import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 25 }, (_, i) => i); // 0..24 for boundary picking

export default function ScheduleEditor({ listing }) {
  const [rows, setRows] = useState([]); // {weekday, enabled, start_hour, end_hour}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!listing?.id) return;
    setLoading(true);
    api.get(`/listings/${listing.id}/schedule`).then((r) => {
      const byDay = {};
      for (const b of r.data) byDay[b.weekday] = b;
      const initial = DAYS.map((_, i) => ({
        weekday: i,
        enabled: !!byDay[i] || Object.keys(byDay).length === 0, // default: enabled if empty schedule
        start_hour: byDay[i]?.start_hour ?? 9,
        end_hour: byDay[i]?.end_hour ?? 17,
      }));
      // If truly empty schedule (host never set), reset to default weekday hours all enabled
      if (Object.keys(byDay).length === 0) {
        for (const row of initial) { row.enabled = row.weekday < 5; }
      }
      setRows(initial);
    }).finally(() => setLoading(false));
  }, [listing?.id]);

  const save = async () => {
    setSaving(true);
    const blocks = rows.filter((r) => r.enabled).map((r) => ({
      weekday: r.weekday,
      start_hour: Number(r.start_hour),
      end_hour: Number(r.end_hour),
    }));
    for (const b of blocks) {
      if (b.start_hour >= b.end_hour) {
        toast.error(`${DAYS[b.weekday]}: end must be after start`);
        setSaving(false);
        return;
      }
    }
    try {
      await api.put(`/listings/${listing.id}/schedule`, { blocks });
      toast.success("Availability saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-48 w-full"/>;

  return (
    <div className="border border-neutral-200">
      <div className="p-5 border-b border-neutral-200">
        <div className="font-display font-medium text-lg">{listing.title}</div>
        <div className="text-xs text-neutral-500 mt-1">Set which hours you're available each weekday. Guests can only book inside these windows.</div>
      </div>
      <div className="divide-y divide-neutral-200">
        {rows.map((r, i) => (
          <div key={r.weekday} className="p-4 flex items-center gap-4">
            <label className="flex items-center gap-2 w-24 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid={`sched-day-${r.weekday}`}
                checked={r.enabled}
                onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))}
                className="w-4 h-4 accent-neutral-900"
              />
              <span className="font-medium">{DAYS[r.weekday]}</span>
            </label>
            {r.enabled ? (
              <div className="flex items-center gap-2 text-sm">
                <Select value={String(r.start_hour)} onValueChange={(v) => setRows(rows.map((x, j) => j === i ? { ...x, start_hour: Number(v) } : x))}>
                  <SelectTrigger data-testid={`sched-start-${r.weekday}`} className="h-9 w-24 border-neutral-300"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {HOURS.slice(0, 24).map((h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2,"0")}:00</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-neutral-400">to</span>
                <Select value={String(r.end_hour)} onValueChange={(v) => setRows(rows.map((x, j) => j === i ? { ...x, end_hour: Number(v) } : x))}>
                  <SelectTrigger data-testid={`sched-end-${r.weekday}`} className="h-9 w-24 border-neutral-300"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {HOURS.slice(1).map((h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2,"0")}:00</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <span className="text-sm text-neutral-400">Closed</span>
            )}
          </div>
        ))}
      </div>
      <div className="p-4 border-t border-neutral-200 flex justify-end">
        <Button data-testid={`save-schedule-${listing.id}`} onClick={save} disabled={saving} className="h-10 bg-neutral-900 hover:bg-neutral-800">
          {saving ? "Saving..." : "Save schedule"}
        </Button>
      </div>
    </div>
  );
}
